/** istanbul ignore file */

import { PropsWithChildren, useEffect, useMemo, useState } from "react";
import { Point } from "pixi.js";
import { Viewport } from "pixi-viewport";
import { CircularProgress } from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "notistack";
import { useSelector } from "../../../../../app/helpers";
import { RootState } from "../../../../../app/rootReducer";
import { DisplayViewport } from "../../DisplayViewport/DisplayViewport";
import { ZoneContainer } from "../../Zone/ZoneContainer";
import { GhostZoneContainer } from "../../GhostZone/GhostZoneContainer";
import { PlaceContainer } from "../../Place/PlaceContainer";
import { IFloorPayload } from "../../../Domain/Types/FloorPlan/FloorPayload.type";
import { DrawZone } from "./Tools/DrawZone/DrawZone";
import { DrawPlaces } from "./Tools/DrawPlaces/DrawPlaces";
import {
  FloorPlanUpdateStatus,
  generateNegativeIds,
  useFloorplan
} from "./Tools/useFloorplan/useFloorplan";
import { useBackgroundImage } from "../../../Hooks/useBackgroundImage";
import { FloorPlanBackground } from "../../FloorPlanBackground/FloorPlanBackground";
import { TopToolbar } from "../../Toolbars/TopToolbar/TopToolbar";
import { QrCodeComponent } from "../../Modals/QrCodeModal/QrCodeComponent";
import { SideToolbar } from "../../Toolbars/SideToolbar/SideToolbar";
import { useSelection } from "../../../Hooks/useSelection/useSelection";
import { ZoneSelectionBorder } from "./Tools/ZoneSelectionBorder/ZoneSelectionBorder";
import { ITimeframe } from "../../../Domain/Types/FloorPlan/Timeframe.type";
import { useAssets } from "../../../Hooks/useAssets";
import { useGeometry } from "../../../Hooks/useGeometry";
import { PlaceSideToolbar } from "../../Toolbars/PlaceSideToolbar/PlaceSideToolbar";
import { MultiPlaceInfoSideBar } from "../../Toolbars/InfoSidebar/PlaceInfoSidebar/MultiPlaceInfoSidebar";
import { useCanvasPrint } from "../../../Hooks/useCanvasPrint";
import { PrintFloorModal } from "../../Modals/PrintFloorModal/PrintFloorModalComponent";
import {
  boundingBoxPerPlaceType,
  checkDeskPos,
  checkIfIsEditable,
  checkIfPublishIsAvailable,
  checkPlaceDisabledStatus,
  checkToolAndMouseOverZone,
  checkToolSelection,
  handleClickedZone,
  handleFloorFrameHistory,
  handlePlaceDuplication,
  handleZoneDuplication,
  onClickPlace
} from "./Functions/CreateFloorPlanView.functions";
import { IViewport } from "../../../Domain/Types/FloorPlan/Viewport.type";
import { ZoneSideToolbar } from "../../Toolbars/ZoneSideToolbar/ZoneSideToolbar";
import { ZoneInfoSidebarSwitch } from "../../Toolbars/InfoSidebar/ZoneInfoSidebar/ZoneInfoSidebar.switch";
import { ChangeFloorFrameModal } from "../../Modals/ChangeFloorFrameModal/ChangeFloorFrameModalComponent";
import { PlanViewMode } from "../../PixiViewport/PixiViewport";
import { useRemoteFetchCompanies } from "../../../../../hooks/Remote/Companies/useRemoteFetchCompanies";
import { Stakeholder } from "../../../../../features/FloorManager/typings/permission.entity";
import { useRemoteFetchCompanyUnits } from "../../../../../hooks/Remote/CompanyUnits/useRemoteFetchCompanyUnits";
import { useRemoteFetchUserInfoForAllUsers } from "../../../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { FileObject } from "../../../../../features/FileDropzone/typings/FileObject.type";
import { EmptyBackgroundText } from "../../Canvas/Partials/EmptyBackgroundText.partial";
import { useHandleCopyPaste } from "../../../Hooks/useHandleCopyPaste/useHandleCopyPaste";
import { IPlaceSchedule } from "../../../Domain/Types/FloorPlan/PlaceSchedule";
import { IZoneSchedule } from "../../../Domain/Types/FloorPlan/ZoneSchedule";
import { BottomToolbar } from "../../Toolbars/BottomToolbar/BottomToolbar";
import {
  getFloorPlanCanvasCorner,
  getFloorPlanPlaceScale,
  resolveFloorPlanCanvasSize,
  resolveFloorPlanPlaceScale
} from "../../../Functions/floorPlanCanvas.functions";

export type MovementPos = {
  moveX: number;
  moveY: number;
};

interface Props {
  width: number;
  height: number;
  name: string;
  floorPlan: IFloorPayload;
  selectedTimeframe: ITimeframe;
  selectedFloorId: number;
  locationInventoryId: number;
  backgroundImageUrl: string;
  floorPlanUpdateStatus: FloorPlanUpdateStatus;
  onPublish: (newFloorPlan: IFloorPayload, newFrame?: File) => void;
  onAbort: () => void;
}

export type ToolSelection = "ZONE.NEW" | "DESK.NEW" | "SELECT.BOOK" | "CAPA.REPORT" | undefined;

export type ZoneColorMode = "NORMAL" | "BYDEPARTMENT" | "INDIVIDUAL";

export function CreateFloorPlanView({
  height,
  children,
  name,
  backgroundImageUrl,
  floorPlan,
  selectedTimeframe,
  locationInventoryId,
  onPublish,
  onAbort,
  selectedFloorId,
  floorPlanUpdateStatus
}: PropsWithChildren<Props>) {
  const { enqueueSnackbar } = useSnackbar();

  const { location: selectedLoc } = useSelector((state: RootState) => state.floorManager.selected);
  const { inventory: locInventory } = useSelector(
    (state: RootState) => state.floorManager.locations
  );

  // check wheter location inventory has site id
  const hasSiteId = !!locInventory.find(loc => loc.id === selectedLoc)?.exivoSiteId;

  const isFloorPlanForService =
    !!floorPlan.roomLayoutAssignments &&
    !!floorPlan.cateringMenuAssignments &&
    !!floorPlan.bookableEquipmentAssignments;

  const isEditable: boolean = checkIfIsEditable(selectedTimeframe, isFloorPlanForService);

  const [tool, setTool] = useState<ToolSelection>(undefined);
  const [viewport, setViewport] = useState<undefined | Viewport>(undefined);
  const [qrCodeOpen, setQrCodeOpen] = useState(false);
  const [showId, setShowId] = useState(true);
  const [blockViewportMovement, setBlockViewportMovement] = useState(false);
  const [extractCanvas, setExtractCanvas] = useState<null | HTMLImageElement>(null);
  const [isMouseOverZone, setIsMouseOverZone] = useState(false);
  const [showPlaceAdd, setShowPlaceAdd] = useState(false);
  const [showZoneAdd, setShowZoneAdd] = useState(false);
  const [isMultiPlaces, setIsMultiPlaces] = useState(false);
  const [numberOfPlacesAdd, setNumberOfPlacesAdd] = useState(1);
  const [typeOfPlaces, setTypeOfPlaces] = useState(1);
  const [typeOfZone, setTypeOfZone] = useState(1);
  const [enablePublish, setEnablePublish] = useState(false);
  const [isZnInvModalOpen, setIsZnInvModalOpen] = useState<boolean>(false);
  const [isExtractingCanvas, setIsExtractingCanvas] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isChangeFrameModalOpen, setIsChangeFrameModalOpen] = useState(false);
  const [zoneColorMode, setZoneColorMode] = useState<ZoneColorMode>("NORMAL");

  const [viewportDimensions, setViewportDimensions] = useState<IViewport>({ width: 0, height: 0 });
  const [placeScale, setPlaceScale] = useState(0);
  const [newFloorFrame, setNewFloorFrame] = useState<File | undefined>(undefined);

  // Number of touches observed at the most recent stage touchstart.
  const [activeTouchCount, setActiveTouchCount] = useState(0);
  // copied objects for copy & paste, duplications
  const [copiedPlaces, setCopiedPlaces] = useState<IPlaceSchedule[]>([]);
  const [copiedZones, setCopiedZones] = useState<IZoneSchedule[]>([]);

  const { isCopyPressed, isPastePressed, resetKeyPressed } = useHandleCopyPaste();
  const [isFieldEditing, setIsFieldEditing] = useState(false);

  // download and set the background image here
  const { background, updateBackgroundImage, unsetBackgroundImage, isEmptyBgUsed } =
    useBackgroundImage({
      backgroundImageUrl,
      viewport: floorPlan.viewport
    });

  // load knob icons assets
  const { knobIcons } = useAssets();

  const { convertPixiPoints } = useGeometry();

  const { data: remoteCompanies, isFetching: isRemoteCompaniesFetching } =
    useRemoteFetchCompanies();
  const { data: remoteCompanyUnits, isFetching: isRemoteCompanyUnitsFetching } =
    useRemoteFetchCompanyUnits(Stakeholder.CostCenterUnit);
  const { data: remoteUsers, isFetching: isRemoteUsersFetching } =
    useRemoteFetchUserInfoForAllUsers();

  const { printCanvas } = useCanvasPrint(name, extractCanvas);

  // handle automatically unselecting and pressing shift
  const {
    selectDesk,
    selectDesks,
    selectedDesks,
    wipeSelection,
    removeDeskFromSelection,
    removeZoneFromSelection,
    selectZone,
    selectZones,
    selectedZones
  } = useSelection(tool);

  const {
    floorPlan: currentFloorPlan,
    addZone,
    transFormDesks,
    stretchDesks,
    deleteDesk,
    addDesks,
    duplicateDesks,
    duplicateZones,
    deleteZone,
    transformZone,
    changeZone,
    changeDesk,
    editConferenceService,
    updateZoneInventory,
    updatePlaceInventory,
    inventoryManagement,
    categoryManagement,
    dispatchFloorPlan,
    canUndo,
    canRedo,
    equipmentManagement,
    editFloorFrame
  } = useFloorplan(
    floorPlan || {
      floorInventoryId: selectedFloorId,
      freeInventories: {
        places: [],
        zones: []
      },
      zones: [],
      places: []
    },
    selectedTimeframe,
    floorPlanUpdateStatus
  );

  const highlightedPlace = currentFloorPlan.places.filter(workplace =>
    selectedZones.includes(workplace.zoneScheduleId)
  );

  const handlePrintFloor = () => {
    // update the canvas capture scene
    wipeSelection();
    setIsPrintModalOpen(true);
  };

  /* istanbul ignore next */
  const handleUpdateFloorFrame = (newFrame: FileObject, newViewport: IViewport) => {
    updateBackgroundImage(newFrame.data as string);
    editFloorFrame(newFrame.data as string, newViewport);
    setNewFloorFrame(newFrame.file);
    setIsExtractingCanvas(true);
  };

  function onClickZone(selectedZoneId: number, mousePos: Point) {
    const deskPos = viewport?.toWorld(mousePos.x, mousePos.y);

    checkDeskPos(deskPos);

    handleClickedZone(
      tool,
      selectedZoneId,
      selectedTimeframe,
      { deskPos, numberOfPlacesAdd, placeScale, typeOfPlaces },
      addDesks,
      selectZone
    );
  }

  const renderDuplication = (type: "place" | "zone", copied: any[]) => {
    const basicOpts = { start: selectedTimeframe.start, end: selectedTimeframe.end };
    if (type === "place") {
      duplicateDesks(basicOpts, ...copied);
      setCopiedPlaces(copied as IPlaceSchedule[]);
      selectDesks(copied.filter(co => co.id !== undefined).map(co => co.id as number));
    } else {
      duplicateZones(basicOpts, ...copied);
      setCopiedZones(copied as IZoneSchedule[]);
      selectZones(copied.filter(co => co.id !== undefined).map(co => co.id as number));
    }
  };

  const onKeyCopy = () => {
    if (selectedDesks.length) {
      setCopiedPlaces(
        currentFloorPlan.places.filter(workplace => selectedDesks.includes(workplace.id))
      );
    }
    if (selectedZones.length) {
      setCopiedZones(currentFloorPlan.zones.filter(zone => selectedZones.includes(zone.id)));
    }
  };

  const onKeyPaste = () => {
    if (selectedDesks.length) {
      const copied = handlePlaceDuplication(currentFloorPlan, copiedPlaces, placeScale);
      renderDuplication("place", copied);
    }
    if (selectedZones.length) {
      const copied = handleZoneDuplication(currentFloorPlan, copiedZones);
      renderDuplication("zone", copied);
    }
    resetKeyPressed();
  };

  const onTapDuplicationKnob = (type: "place" | "zone") => {
    let copied: any[] = [];
    if (type === "place") {
      const obj = currentFloorPlan.places.filter(workplace => selectedDesks.includes(workplace.id));
      copied = handlePlaceDuplication(currentFloorPlan, obj, placeScale);
    } else {
      const obj = currentFloorPlan.zones.filter(zone => selectedZones.includes(zone.id));
      copied = handleZoneDuplication(currentFloorPlan, obj);
    }
    renderDuplication(type, copied);
  };

  useEffect(() => {
    if (!background) return;

    if (isEmptyBgUsed) {
      setPlaceScale(
        getFloorPlanPlaceScale({ width: 400, height: background.height }, currentFloorPlan.viewport)
      );
      return;
    }

    setPlaceScale(resolveFloorPlanPlaceScale(currentFloorPlan, background));
  }, [background, currentFloorPlan, isEmptyBgUsed]);

  const canvasSize = useMemo(() => {
    if (!background || isEmptyBgUsed) {
      return currentFloorPlan.viewport;
    }

    return resolveFloorPlanCanvasSize(currentFloorPlan, background);
  }, [background, currentFloorPlan, isEmptyBgUsed]);

  useEffect(() => {
    // unblock the viewport in case it is still blocked for some reason
    checkToolSelection(tool, setBlockViewportMovement);
    // wipe place selection to avoid confusion
    wipeSelection();
  }, [tool]);

  useEffect(() => {
    // check every places and zones have both inventory & category. and if not, cannot publish the floor plan
    checkIfPublishIsAvailable(currentFloorPlan, setEnablePublish);

    // after adding new floorFrame, when back to original background or back again to new floor frame
    handleFloorFrameHistory(
      currentFloorPlan,
      backgroundImageUrl,
      newFloorFrame,
      updateBackgroundImage
    );
  }, [currentFloorPlan]);

  // update the canvas capture scene after selection & changes are occured
  useEffect(() => {
    if (selectedDesks.length >= 1 || selectedZones.length >= 1) setIsExtractingCanvas(false);
    else setIsExtractingCanvas(true);
  }, [selectedDesks, selectedZones]);

  useEffect(() => {
    // to avoid intefering with editing the text field actions
    if (isEditable && !isFieldEditing && isCopyPressed) onKeyCopy();
  }, [isCopyPressed, isFieldEditing]);

  useEffect(() => {
    // to avoid intefering with editing the text field actions
    if (isEditable && !isFieldEditing && isPastePressed) onKeyPaste();
  }, [isPastePressed, isFieldEditing]);

  return (
    <>
      {!background && (
        <CircularProgress size={50} sx={{ position: "absolute", top: "48%", left: "48%" }} />
      )}

      {background && !!placeScale && (
        <DisplayViewport
          planViewMode={PlanViewMode.CREATE}
          onInit={v => setViewport(v)}
          displayTopToolbar
          floorPlanName={name}
          initialZoomEnd={getFloorPlanCanvasCorner(canvasSize)} // initCorner
          blockViewportMovement={blockViewportMovement}
          onExtract={instance => setExtractCanvas(instance)}
          isExtractingCanvas={isExtractingCanvas}
          setIsExtractingCanvas={setIsExtractingCanvas}
          currentFloorPlan={currentFloorPlan}
          viewportDimensions={viewportDimensions}
          setViewportDimensions={setViewportDimensions}
          setActiveTouchCount={setActiveTouchCount}
          placeScale={placeScale}
          toolbars={
            <>
              {/* top bar to select other functions */}
              <TopToolbar
                floorPlanName={name}
                floorPlan={currentFloorPlan}
                qrCodeOpen={qrCodeOpen}
                setQrCodeOpen={o => setQrCodeOpen(o)}
                showId={showId}
                setShowId={s => setShowId(s)}
                onClickPublish={() => {
                  onPublish(currentFloorPlan, newFloorFrame);
                  wipeSelection();
                }}
                dispatchFloorPlan={dispatchFloorPlan}
                canUndo={canUndo}
                canRedo={canRedo}
                onClickAbort={() => {
                  onAbort();
                  unsetBackgroundImage();
                }}
                printFloorPlan={handlePrintFloor}
                isEditable={isEditable}
                enablePublish={enablePublish}
                isFloorPlanForService={isFloorPlanForService}
                changeFloorFrame={() => setIsChangeFrameModalOpen(true)}
                wipeSelection={wipeSelection}
                remoteUnits={{
                  remoteCompanies: remoteCompanies,
                  remoteCompanyUnits: remoteCompanyUnits,
                  remoteUsers: remoteUsers
                }}
                zoneColorMode={zoneColorMode}
                setZoneColorMode={setZoneColorMode}
              />

              {/* side bar */}
              {isEditable ? (
                <>
                  {/* side bar to select tools */}
                  <SideToolbar
                    selectedTool={tool}
                    onSelectTool={t => setTool(t)}
                    setShowPlaceAdd={p => setShowPlaceAdd(p)}
                    setIsMultiPlaces={m => setIsMultiPlaces(m)}
                    setTypeOfPlaces={t => setTypeOfPlaces(t)}
                    setNumberOfPlacesAdd={a => setNumberOfPlacesAdd(a)}
                    setShowZoneAdd={z => setShowZoneAdd(z)}
                    setTypeOfZone={t => setTypeOfZone(t)}
                    isFloorPlanForService={isFloorPlanForService}
                  />
                  {/* create new places single or multi */}
                  {showPlaceAdd && (
                    <PlaceSideToolbar
                      isMultiPlaces={isMultiPlaces}
                      setIsMultiPlaces={m => setIsMultiPlaces(m)}
                      setNumberOfPlacesAdd={n => setNumberOfPlacesAdd(n)}
                      typeOfPlaces={typeOfPlaces}
                      setTypeOfPlaces={t => setTypeOfPlaces(t)}
                    />
                  )}
                  {/* create new zone and select type */}
                  {showZoneAdd && (
                    <ZoneSideToolbar
                      typeOfZone={typeOfZone}
                      setTypeOfZone={t => setTypeOfZone(t)}
                      availableTypes={currentFloorPlan.zoneTypes}
                    />
                  )}
                </>
              ) : null}

              {zoneColorMode === "BYDEPARTMENT" && (
                <BottomToolbar departmentData={remoteCompanyUnits} />
              )}

              {selectedDesks.length > 0 && (
                <MultiPlaceInfoSideBar
                  height={height}
                  placeSchedules={currentFloorPlan.places.filter(workplace =>
                    selectedDesks.includes(workplace.id)
                  )}
                  floorPlan={currentFloorPlan}
                  locationInventoryId={locationInventoryId}
                  onChangeTransform={(deskIds, t) => /* istanbul ignore next */ {
                    transFormDesks({ ids: deskIds, transform: t });
                  }}
                  onChangeStretch={(deskIds, t) => stretchDesks({ ids: deskIds, stretch: t })}
                  onClickDelete={deskIds => {
                    for (const id of deskIds) removeDeskFromSelection(id);
                    deleteDesk(deskIds);
                  }}
                  onCloseDesk={() => wipeSelection()}
                  onChangeName={(ids, newDeskName) => changeDesk(ids, { name: newDeskName })}
                  onChangeDescription={(ids, desc) => changeDesk(ids, { description: desc })}
                  onChangeEnabledStatus={(ids, status) => changeDesk(ids, { enabled: status })}
                  onChangePerm={(ids, newPerms) => changeDesk(ids, { perms: newPerms })}
                  inventoryManagement={inventoryManagement}
                  categoryManagement={categoryManagement}
                  equipmentManagement={equipmentManagement}
                  updatePlaceInventory={updatePlaceInventory}
                  floorPlanName={name}
                  onChangeApproval={(ids, approval) => changeDesk(ids, { approval: approval })}
                  onChangeEquipCate={(ids, equipCate) =>
                    changeDesk(ids, { equipmentCategories: equipCate })
                  }
                  onChangeBookProp={(ids, bookProps) =>
                    changeDesk(ids, { bookingProperties: bookProps })
                  }
                  remoteUnits={{
                    remoteCompanies: remoteCompanies,
                    remoteCompanyUnits: remoteCompanyUnits,
                    remoteUsers: remoteUsers
                  }}
                  isRemoteUnitsFetching={
                    isRemoteCompaniesFetching ||
                    isRemoteCompanyUnitsFetching ||
                    isRemoteUsersFetching
                  }
                  isEditable={isEditable}
                  setIsFieldEditing={setIsFieldEditing}
                />
              )}

              {selectedZones.length > 0 && (
                <ZoneInfoSidebarSwitch
                  updateZoneInventory={updateZoneInventory}
                  height={height}
                  locationInventoryId={locationInventoryId}
                  zoneSchedules={currentFloorPlan.zones.filter(zone =>
                    selectedZones.includes(zone.id)
                  )}
                  floorPlan={currentFloorPlan}
                  inventoryManagement={inventoryManagement}
                  categoryManagement={categoryManagement}
                  equipmentManagement={equipmentManagement}
                  isZnInvModalOpen={isZnInvModalOpen}
                  hasSiteId={hasSiteId}
                  floorPlanName={name}
                  setIsZnInvModalOpen={(o: boolean) => setIsZnInvModalOpen(o)}
                  isFloorPlanForService={isFloorPlanForService}
                  onClose={wipeSelection}
                  onClickDelete={ids => {
                    for (const id of ids) removeZoneFromSelection(id);
                    wipeSelection();
                    deleteZone(ids);
                  }}
                  onChangeZoneDescription={description =>
                    changeZone(selectedZones, { description })
                  }
                  onChangeName={(ids, newZoneName) => changeZone(ids, { name: newZoneName })}
                  onChangeEnabledStatus={(ids, status) => changeZone(ids, { enabled: status })}
                  onChangePerm={(ids, newPerms) => changeZone(ids, { perms: newPerms })}
                  onChangeUserNumber={(ids, newUserNumber) =>
                    changeZone(ids, { numberOfUser: newUserNumber })
                  }
                  onChangeConfService={(
                    id,
                    { roomlayouts, bookableEquipments, cateringServices }
                  ) =>
                    editConferenceService(id, {
                      roomlayouts: roomlayouts,
                      bookableEquipments: bookableEquipments,
                      cateringServices: cateringServices
                    })
                  }
                  onChangeApproval={(ids, approval) => changeZone(ids, { approval: approval })}
                  onChangeEquipCate={(ids, equipCate) =>
                    changeZone(ids, { equipmentCategories: equipCate })
                  }
                  onChangeBookProp={(ids, bookProps) =>
                    changeZone(ids, { bookingProperties: bookProps })
                  }
                  remoteUnits={{
                    remoteCompanies: remoteCompanies,
                    remoteCompanyUnits: remoteCompanyUnits,
                    remoteUsers: remoteUsers
                  }}
                  isRemoteUnitsFetching={
                    isRemoteCompaniesFetching ||
                    isRemoteCompanyUnitsFetching ||
                    isRemoteUsersFetching
                  }
                  isEditable={isEditable}
                  setIsFieldEditing={setIsFieldEditing}
                />
              )}

              {/** print QR Code for workplaces */}
              <QrCodeComponent
                open={qrCodeOpen}
                floorPlanName={name}
                entries={currentFloorPlan.places}
                onClose={() => setQrCodeOpen(false)}
              />
              {/** print the floor plan */}
              <PrintFloorModal
                isPrintModalOpen={isPrintModalOpen}
                setIsPrintModalOpen={setIsPrintModalOpen}
                printFloor={() => {
                  printCanvas();
                  setIsPrintModalOpen(false);
                }}
              />

              {/* change the background image of floor frame */}
              <ChangeFloorFrameModal
                isChangeFrameModalOpen={isChangeFrameModalOpen}
                setIsChangeFrameModalOpen={setIsChangeFrameModalOpen}
                floorPlan={currentFloorPlan}
                updateFloorFrame={handleUpdateFloorFrame}
              />
            </>
          }
        >
          {/** Background image, usually a floor plan */}
          {!isEmptyBgUsed && (
            <FloorPlanBackground
              texture={background}
              floorPlan={currentFloorPlan}
              name="background-spr"
              data-testid="background-spr"
            />
          )}
          {isEmptyBgUsed && (
            <EmptyBackgroundText
              rectWidth={canvasSize.width}
              rectHeight={canvasSize.height}
              posY={60}
            />
          )}

          {/** draw zones container minus the selected one */}
          {currentFloorPlan.zones
            .filter(z => !selectedZones.includes(z.id))
            .map(zone => (
              <ZoneContainer
                key={uuidv4()}
                viewport={viewport}
                id={zone.id}
                inventoryId={zone.inventory?.id || zone.inventoryId}
                walls={convertPixiPoints(zone.coordinates)}
                onClick={onClickZone}
                onPointerEnter={() => checkToolAndMouseOverZone(tool, "Enter", setIsMouseOverZone)}
                onPointerLeave={() => checkToolAndMouseOverZone(tool, "Leave", setIsMouseOverZone)}
                showId={showId}
                // isClickable={isEditable}
                isClickable
                disabled={zone.disabled}
                zoneType={zone.zoneTypeId || zone.inventory?.zoneTypeId || 1}
                activeTouchCount={activeTouchCount}
                tool={tool}
                zoneColorMode={zoneColorMode}
                departmentData={remoteCompanyUnits}
                assignedPlaces={currentFloorPlan.places.filter(
                  workplace => workplace.zoneScheduleId === zone.id
                )}
                permissions={zone.permissions}
              />
            ))}

          {/** ghost zone when a zone is selected, to move zones */}
          {selectedZones.length > 0 && (
            <GhostZoneContainer
              initialZones={currentFloorPlan.zones.filter(z => selectedZones.includes(z.id))}
              isEditable={isEditable}
              knobIcons={knobIcons}
              viewport={viewport}
              showId={showId}
              onDuplicate={() => onTapDuplicationKnob("zone")}
              isFloorPlanForService={isFloorPlanForService}
              onZoneWallsChange={zoneTransforms => transformZone(...zoneTransforms)}
              onClick={onClickZone}
              zoneColorMode={zoneColorMode}
              departmentData={remoteCompanyUnits}
              assignedPlaces={currentFloorPlan.places.filter(workplace =>
                selectedZones.includes(workplace.zoneScheduleId)
              )}
            />
          )}

          {/** draw places minus the selected ones */}
          {currentFloorPlan.places
            .filter(r => !selectedDesks.includes(r.id))
            .map(workplace => (
              <PlaceContainer
                key={uuidv4()}
                boundingBox={{
                  width: boundingBoxPerPlaceType(workplace).width,
                  height: boundingBoxPerPlaceType(workplace).height
                }}
                id={workplace.id}
                inventoryId={workplace.inventoryId}
                rotation={workplace.rotate}
                position={workplace.position}
                variant={checkPlaceDisabledStatus(workplace.disabled)}
                isSelectable={tool === undefined && !isFloorPlanForService}
                isSelected={selectedDesks.includes(workplace.id)}
                onClick={() => onClickPlace(workplace.id, tool, selectDesk)}
                showId={showId}
                isHighlighted={highlightedPlace.map(h => h.id).includes(workplace.id)}
                placeTypeId={workplace.placeTypeId || workplace.inventory?.placeTypeId}
                placeScale={placeScale}
                activeTouchCount={activeTouchCount}
                tool={tool}
              />
            ))}

          {/** creates a box containing a copy of selected places so a user can transform them quickly */}
          {viewport && tool === undefined && selectedDesks.length > 0 && (
            <ZoneSelectionBorder
              initialDesks={currentFloorPlan.places.filter(wp => selectedDesks.includes(wp.id))}
              viewport={viewport}
              knobIcons={knobIcons}
              onTransformPlace={objectTransforms => transFormDesks(...objectTransforms)}
              onStretch={objectStretch => stretchDesks(...objectStretch)}
              onDuplicate={() => onTapDuplicationKnob("place")}
              onClickDesk={deskId => onClickPlace(deskId, tool, selectDesk)}
              isEditable={isEditable}
              showId={showId}
              placeScale={placeScale}
            />
          )}

          {/** create new zone */}
          {tool === "ZONE.NEW" && (
            <DrawZone
              tool={tool}
              onCreateZone={zone => {
                addZone(zone, selectedTimeframe, typeOfZone);
                selectZone(generateNegativeIds(currentFloorPlan.zones));
                setIsZnInvModalOpen(true);
              }}
              viewport={viewport}
              enqueueSnackbar={enqueueSnackbar}
            />
          )}

          {/** create new places */}
          {tool === "DESK.NEW" && isMouseOverZone && numberOfPlacesAdd > 0 && (
            <DrawPlaces
              viewport={viewport}
              numberOfPlacesAdd={numberOfPlacesAdd}
              placeTypeId={typeOfPlaces}
              placeScale={placeScale}
            />
          )}

          {/** additional containers for extra features if needed */}
          {children}
        </DisplayViewport>
      )}
    </>
  );
}
